import { z } from "zod";

/**
 * Contrato de branding por institución (ADR 038 / ADR 039 — Fase 0 del SaaS).
 *
 * Es **wire** desde el día uno: en Fase 0 el branding sale de una constante
 * local (`BRANDING_DEFAULT`), pero en Fase 1 vendrá del backend por subdominio.
 * Por ADR 002 el contrato nace como schema Zod en `@copi/shared` para no
 * refactorizar tipos cuando se conecte la fuente remota.
 *
 * Los colores van en formato `"R G B"` (tres enteros 0-255 separados por un
 * espacio) para encajar **sin conversión** con los tokens CSS de
 * `apps/web/src/styles/globals.css`, que ya usan ese formato para soportar
 * `rgb(var(--brand-500) / <alpha>)`.
 */

/** Regex de un triplete RGB `"R G B"` (sin validar rango; eso lo hace el refine). */
const TRIPLETE_RGB_RE = /^\d{1,3} \d{1,3} \d{1,3}$/;

/**
 * Valida un color en formato `"R G B"` con cada componente en [0, 255].
 * Ej. válido: `"31 138 76"`. Inválido: `"300 0 0"`, `"#1f8a4c"`, `"31,138,76"`.
 */
export const rgbTripletSchema = z
  .string()
  .regex(TRIPLETE_RGB_RE, 'Color debe tener formato "R G B" (ej. "31 138 76")')
  .refine((valor) => valor.split(" ").every((componente) => Number(componente) <= 255), {
    message: "Cada componente RGB debe estar entre 0 y 255",
  });

/**
 * Tokens de color de una institución (ADR 047). Dos capas:
 *
 *  - **Rampa de marca** `brand50..900` + acento `gold500`: la identidad. La
 *    deriva `derivarPaleta` desde el color principal del usuario.
 *  - **Acentos semánticos afinables** `success*` / `info*`: pintan los badges de
 *    estado "aprobado" / "en revisión". Tienen contraste WCAG garantizado.
 *
 * Los 7 tokens originales (`brand50/100/500/600/700/900`, `gold500`) son
 * **requeridos** para back-compat: filas creadas antes del ADR 047 sólo tienen
 * estos y deben seguir validando. Los tokens nuevos son **opcionales**; al leer,
 * `normalizarColores` rellena los faltantes derivándolos. El resto de la paleta
 * (neutros, warning/error universales) NO se personaliza — vive en `globals.css`.
 */
export const brandingColoresSchema = z.object({
  // Rampa de marca — los 7 originales son requeridos (back-compat).
  brand50: rgbTripletSchema,
  brand100: rgbTripletSchema,
  brand200: rgbTripletSchema.optional(),
  brand300: rgbTripletSchema.optional(),
  brand400: rgbTripletSchema.optional(),
  brand500: rgbTripletSchema,
  brand600: rgbTripletSchema,
  brand700: rgbTripletSchema,
  brand800: rgbTripletSchema.optional(),
  brand900: rgbTripletSchema,
  gold500: rgbTripletSchema,
  // Acentos semánticos afinables (ADR 047) — opcionales; default verde/azul.
  success50: rgbTripletSchema.optional(),
  success400: rgbTripletSchema.optional(),
  success500: rgbTripletSchema.optional(),
  success600: rgbTripletSchema.optional(),
  success700: rgbTripletSchema.optional(),
  info50: rgbTripletSchema.optional(),
  info400: rgbTripletSchema.optional(),
  info500: rgbTripletSchema.optional(),
  info600: rgbTripletSchema.optional(),
  info700: rgbTripletSchema.optional(),
  /**
   * Color de fondo SEMILLA (ADR 050) — opcional. Aporta SÓLO el tono: el sistema
   * deriva las superficies neutras (fondo/card/muted/borde) con la luminosidad
   * fija por tema (`derivarNeutrales`), de modo que el contraste del texto queda
   * garantizado en claro y oscuro pase lo que pase con el color. Ausente ⇒
   * neutros navy por defecto (estilo Tesela, ADR 049).
   */
  fondo: rgbTripletSchema.optional(),
});
export type BrandingColores = z.infer<typeof brandingColoresSchema>;

/**
 * Paleta **resuelta**: todos los tokens presentes (sin opcionales). Es lo que
 * produce `derivarPaleta` / `normalizarColores` y lo que consume el frontend al
 * inyectar las CSS variables. Garantiza que `applyBranding` nunca encuentre un
 * token faltante.
 */
export const brandingColoresResueltoSchema = z.object({
  brand50: rgbTripletSchema,
  brand100: rgbTripletSchema,
  brand200: rgbTripletSchema,
  brand300: rgbTripletSchema,
  brand400: rgbTripletSchema,
  brand500: rgbTripletSchema,
  brand600: rgbTripletSchema,
  brand700: rgbTripletSchema,
  brand800: rgbTripletSchema,
  brand900: rgbTripletSchema,
  gold500: rgbTripletSchema,
  success50: rgbTripletSchema,
  success400: rgbTripletSchema,
  success500: rgbTripletSchema,
  success600: rgbTripletSchema,
  success700: rgbTripletSchema,
  info50: rgbTripletSchema,
  info400: rgbTripletSchema,
  info500: rgbTripletSchema,
  info600: rgbTripletSchema,
  info700: rgbTripletSchema,
  // Color de fondo semilla (ADR 050): único token que sigue siendo opcional en
  // la paleta resuelta — ausente significa "neutros navy por defecto".
  fondo: rgbTripletSchema.optional(),
});
export type BrandingColoresResuelto = z.infer<typeof brandingColoresResueltoSchema>;

/** Estilo de la barra lateral: clara (neutra) u oscura con el color de marca. */
export const estiloSidebarSchema = z.enum(["claro", "oscuro"]);
export type EstiloSidebar = z.infer<typeof estiloSidebarSchema>;

/**
 * Branding completo de una institución. `logoUrl = null` ⇒ el frontend usa el
 * placeholder por defecto (cuadro de color de marca), evitando un `<img>` roto.
 */
export const brandingSchema = z.object({
  /** Nombre largo del sistema. Ej. "Sistema de Gestión de Tesis" (login, títulos). */
  nombreSistema: z.string().min(1),
  /** Nombre compacto para espacios estrechos (sidebar). Ej. "Sistema Tesis". */
  nombreSistemaCorto: z.string().min(1),
  /** Nombre legal de la institución. Ej. "Universidad Nacional Hermilio Valdizán". */
  nombreInstitucion: z.string().min(1),
  /** Sigla institucional. Ej. "UNHEVAL". */
  nombreCorto: z.string().min(1),
  logoUrl: z.string().url().nullable(),
  colores: brandingColoresSchema,
  /**
   * Estilo de la barra lateral (ADR 047). `claro` = superficie neutra (default,
   * back-compat para filas sin el campo); `oscuro` = fondo con el color de marca
   * (`brand-800/900`) y texto claro. Es decisión de branding del tenant, no una
   * preferencia del usuario (eso es el modo claro/oscuro, ADR 048).
   */
  sidebar: estiloSidebarSchema.default("claro"),
});
export type Branding = z.infer<typeof brandingSchema>;

/**
 * Branding por defecto (UNHEVAL). Replica EXACTAMENTE los valores que viven hoy
 * en `apps/web/src/styles/globals.css` (`:root`), de modo que una institución
 * sin branding propio cae a este sin cambio visual. `globals.css` se mantiene
 * como fallback de primer render (anti-flash) — esta constante es la fuente
 * para la inyección en runtime.
 */
export const BRANDING_DEFAULT: Branding = {
  nombreSistema: "Sistema de Gestión de Tesis",
  nombreSistemaCorto: "Sistema Tesis",
  nombreInstitucion: "Universidad Nacional Hermilio Valdizán",
  nombreCorto: "UNHEVAL",
  logoUrl: null,
  // Default del SaaS = paleta "Tesela" (ADR 049): primario azul + acento teal.
  // Las instituciones con branding propio (UNHEVAL/UPLA/UDH) conservan el suyo;
  // esto es sólo el fallback (localhost / institución sin branding cargado).
  colores: {
    brand50: "238 242 253",
    brand100: "223 231 251",
    brand500: "44 92 230",
    brand600: "36 75 189",
    brand700: "27 57 143",
    brand900: "13 28 69",
    gold500: "18 181 163",
  },
  sidebar: "claro",
};

/**
 * Respuesta del endpoint público `GET /tenant-config?slug=<slug>` (ADR 040).
 * El frontend la consume antes de autenticar para resolver el branding por
 * subdominio. `slug` confirma qué institución resolvió el backend (o "default"
 * si el slug no existe y se devolvió el branding por defecto).
 */
export const tenantConfigSchema = z.object({
  slug: z.string().min(1),
  branding: brandingSchema,
});
export type TenantConfig = z.infer<typeof tenantConfigSchema>;

/**
 * Query del endpoint `GET /tenant-config`. El slug se deriva del subdominio en
 * el frontend. Sin slug (acceso por dominio plano / localhost), el backend
 * devuelve el branding por defecto.
 */
export const tenantConfigQuerySchema = z.object({
  // Slug ausente o vacío (localhost / dominio plano) es válido: el backend
  // degrada al branding por defecto. Si viene, debe ser un label DNS válido.
  slug: z
    .string()
    .trim()
    .max(63) // límite de label DNS
    .regex(/^[a-z0-9-]*$/, "slug inválido") // '*' permite cadena vacía
    .optional()
    .default(""),
});
export type TenantConfigQueryInput = z.infer<typeof tenantConfigQuerySchema>;
